import {
  AVTimecodeInfoDto,
  AudioVideoMetadataDto,
  DocumentMetadataDto,
  ImageMetadataDto,
  VideoFramesInfoDto,
  WebsiteMetadataDto
} from '@api/Api';

export type AssetMetadataDto =
  | AudioVideoMetadataDto
  | ImageMetadataDto
  | DocumentMetadataDto
  | WebsiteMetadataDto;

type VideoScrubInfo = {
  scrubsUrl: string;
  frameWidth: number;
  frameHeight: number;
  framesCount: number;
};

export function isAudioVideo(
  metadata: AssetMetadataDto | null | undefined
): metadata is AudioVideoMetadataDto {
  return metadata?.type === 'video' || metadata?.type === 'audio';
}

function getVideoFrames(
  metadata: AudioVideoMetadataDto | null | undefined
): { numerator: number; denominator: number } | undefined {
  const numerator = metadata?.info.avInfo?.video?.frames?.numerator;
  const denominator = metadata?.info.avInfo?.video?.frames?.denominator;
  if (!numerator || !denominator) return undefined;
  return { numerator, denominator };
}

export function getVideoFrameRate(
  metadata: AudioVideoMetadataDto | null | undefined
): number | undefined {
  const frames = getVideoFrames(metadata);
  if (!frames) return undefined;
  const value = frames.numerator / frames.denominator;
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return value;
}

export function getMediaDuration(
  metadata: AudioVideoMetadataDto | null | undefined
): number {
  return metadata?.info.avInfo?.duration || 0;
}

/**
 * Calculates the frame number at a given playback time.
 * Uses raw numerator/denominator for precision (avoids float division rounding).
 * Returns `null` when frame rate metadata is unavailable.
 */
export function getFrameAtTime(
  time: number,
  metadata: AssetMetadataDto | null | undefined
): number | null {
  if (!isAudioVideo(metadata)) return null;
  const frames = getVideoFrames(metadata);
  if (!frames) return null;
  return Math.floor((time * frames.numerator) / frames.denominator);
}

/**
 * Converts a frame number into the corresponding playback time in seconds.
 * Uses raw numerator/denominator for precision.
 * Returns `null` when frame rate metadata is unavailable.
 */
export function getTimeAtFrame(
  frame: number,
  metadata: AssetMetadataDto | null | undefined
): number | null {
  if (!isAudioVideo(metadata)) return null;
  const frames = getVideoFrames(metadata);
  if (!frames) return null;
  return (frame * frames.denominator) / frames.numerator;
}

function toTwoDigits(value: number): string {
  return `${value}`.padStart(2, '0');
}

function clampModulo(value: number, modulo: number): number {
  return ((value % modulo) + modulo) % modulo;
}

function toFrameNumber(
  timecode: AVTimecodeInfoDto,
  nominalFps: number
): number {
  const totalMinutes = timecode.hours * 60 + timecode.minutes;
  const totalFrames = Math.trunc(
    (timecode.hours * 3600 + timecode.minutes * 60 + timecode.seconds) *
      nominalFps +
      timecode.frames
  );

  if (!timecode.dropFrame) return totalFrames;

  const droppedFrames = Math.round(nominalFps * 0.0666666667);
  const droppedBeforeMinute =
    droppedFrames * (totalMinutes - Math.floor(totalMinutes / 10));
  return totalFrames - droppedBeforeMinute;
}

function fromFrameNumber(
  frameNumber: number,
  nominalFps: number,
  dropFrame: boolean
): { hours: number; minutes: number; seconds: number; frames: number } {
  if (!dropFrame) {
    const framesPerDay = nominalFps * 60 * 60 * 24;
    const normalized = clampModulo(frameNumber, framesPerDay);
    const hours = Math.floor(normalized / (nominalFps * 3600));
    const minutes = Math.floor(
      (normalized % (nominalFps * 3600)) / (nominalFps * 60)
    );
    const seconds = Math.floor((normalized % (nominalFps * 60)) / nominalFps);
    const frames = normalized % nominalFps;
    return { hours, minutes, seconds, frames };
  }

  const droppedFrames = Math.round(nominalFps * 0.0666666667);
  const framesPerHour = nominalFps * 60 * 60;
  const framesPer24Hours = framesPerHour * 24;
  const framesPer10Minutes = nominalFps * 60 * 10 - droppedFrames * 9;
  const framesPerMinute = nominalFps * 60 - droppedFrames;

  const normalized = clampModulo(frameNumber, framesPer24Hours);
  const tenMinuteChunks = Math.floor(normalized / framesPer10Minutes);
  const framesIntoTenMinuteChunk = normalized % framesPer10Minutes;

  // SMPTE drop-frame mapping: skip frame numbers 00 and 01 at each
  // minute boundary except every 10th minute.
  const droppedBefore =
    droppedFrames * 9 * tenMinuteChunks +
    (framesIntoTenMinuteChunk >= droppedFrames
      ? droppedFrames *
        Math.floor((framesIntoTenMinuteChunk - droppedFrames) / framesPerMinute)
      : 0);

  const timecodeFrameNumber = normalized + droppedBefore;

  const hours = Math.floor(timecodeFrameNumber / framesPerHour) % 24;
  const minutes =
    Math.floor((timecodeFrameNumber % framesPerHour) / (nominalFps * 60)) % 60;
  const seconds =
    Math.floor((timecodeFrameNumber % (nominalFps * 60)) / nominalFps) % 60;
  const frames = timecodeFrameNumber % nominalFps;

  return { hours, minutes, seconds, frames };
}

export function getSmpteTimecodeAtTime(
  currentTime: number,
  avTimecode: AVTimecodeInfoDto | null | undefined,
  videoFrames: VideoFramesInfoDto | null | undefined
): string | null {
  const numerator = videoFrames?.numerator;
  const denominator = videoFrames?.denominator;

  if (!numerator || !denominator) return null;

  const fps = numerator / denominator;
  if (!Number.isFinite(fps) || fps <= 0) return null;

  const nominalFps = Math.round(fps);
  if (nominalFps <= 0) return null;

  const defaultTimecode: AVTimecodeInfoDto = {
    hours: 0,
    minutes: 0,
    seconds: 0,
    frames: 0,
    dropFrame: false
  };

  const startTimecode = avTimecode ?? defaultTimecode;
  const startFrameNumber = toFrameNumber(startTimecode, nominalFps);
  const currentFrameOffset = Math.floor(
    (currentTime * numerator) / denominator
  );
  const absoluteFrameNumber = startFrameNumber + currentFrameOffset;
  const tc = fromFrameNumber(
    absoluteFrameNumber,
    nominalFps,
    !!startTimecode.dropFrame
  );

  const separator = startTimecode.dropFrame ? ';' : ':';
  return `${toTwoDigits(tc.hours)}:${toTwoDigits(tc.minutes)}:${toTwoDigits(
    tc.seconds
  )}${separator}${toTwoDigits(tc.frames)}`;
}

export function getVideoScrubInfo(
  metadata: AssetMetadataDto | null | undefined
): VideoScrubInfo {
  if (!isAudioVideo(metadata) || metadata.type !== 'video') {
    return {
      scrubsUrl: '',
      frameWidth: 0,
      frameHeight: 0,
      framesCount: 0
    };
  }

  return {
    scrubsUrl: metadata.scrub?.url || '',
    frameWidth: metadata.scrub?.frames?.dimensions.width || 0,
    frameHeight: metadata.scrub?.frames?.dimensions.height || 0,
    framesCount: metadata.scrub?.frames?.count || 0
  };
}
